import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedCounter from '../common/AnimatedCounter';

function About() {
  const purposes = [
    {
      title: "Who We Are",
      description:
        "Intellectual Oasis Fellowship is a nonprofit advancing education through research, publishing, scholarships, and practical EdTech tools. We convene a fellowship of scholars and practitioners to produce evidence that improves policy and classroom practice.",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14v7" />
        </svg>
      ),
      stats: { "Active Scholars": "30+", "Partners": "30+", "Regions": "5+" }
    },
    {
      title: "Why We Exist",
      description:
        "Education systems in Nigeria and West Africa face challenges of access, quality, and equity. Intellectual Oasis Fellowship responds by building a research pipeline, mentoring early‑career scholars, and publishing insights that guide policy.",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      stats: { "Papers Published": "35+", "Data Sets Released": "70+", "Projects": "25+" }
    },
    {
      title: "How We Help",
      description:
        "We provide scholarships that expand opportunity and EdTech tools that help students study better. Our open datasets and policy papers support governments, schools, and NGOs.",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      stats: { "Students Reached": "5000+", "Students Supported": "300+", "Schools Engaged": "100+" }
    },
    {
      title: "Where We're Going",
      description:
        "By 2035, Intellectual Oasis Fellowship will operate a data‑rich research repository and collaborative hub that influences policy and improves educational systems across West Africa and beyond.",
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        </svg>
      ),
      stats: { "Countries of Impact": "10+", "Open Repository": "1", "Policy Briefs": "25+" }
    }
  ];

  const parseMetric = (text) => {
    const match = String(text).match(/^(\d+)(.*)$/);
    if (!match) return { num: 0, suffix: '' };
    return { num: parseInt(match[1], 10), suffix: match[2] || '' };
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50" id="about" aria-live="polite" aria-atomic="true">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h4 className="text-[rgb(130,88,18)] font-semibold mb-4">ABOUT US</h4>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Research‑Driven. Learner‑Focused.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We are building West Africa's future hub for education research - connecting scholars, generating open data, and turning insights into better policy and better learning for every student.
          </p>
          <p className="mt-6 text-base sm:text-lg text-gray-700 bg-white inline-block px-4 py-2 rounded-full border border-gray-200" aria-label="2035 long-term goal">
            By 2035, Intellectual Oasis Fellowship will operate Africa’s leading open repository of education research and policy insights.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08 } } }}
          className="grid gap-6 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-12"
        >
          {purposes.map((purpose, idx) => (
            <motion.div 
              key={purpose.title}
              variants={{ hidden: { opacity: 0, x: idx % 2 === 0 ? -40 : 40 }, show: { opacity: 1, x: 0 } }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              whileHover={{ y: -4 }}
              className="bg-white rounded-2xl shadow-xl p-6 sm:p-7 lg:p-8 transition-shadow duration-300 hover:shadow-2xl border border-gray-100 h-full flex flex-col"
              style={{ willChange: 'transform' }}
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="p-3 rounded-full bg-[rgb(130,88,18)]/10 text-[rgb(130,88,18)]" aria-hidden="true">
                  {purpose.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{purpose.title}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">{purpose.description}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-auto">
                {Object.entries(purpose.stats).map(([key, value]) => {
                  const { num, suffix } = parseMetric(value);
                  return (
                    <div key={key} className="text-center p-3 rounded-lg bg-gray-50">
                      <div className="text-xl font-bold text-[rgb(130,88,18)]">
                        <AnimatedCounter to={num} />{suffix}
                      </div>
                      <div className="text-xs text-gray-600">{key}</div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative"
          style={{ contain: 'layout style paint' }}
        >
          <div className="absolute -top-6 -left-6 w-24 h-24 bg-[rgb(130,88,18)]/10 rounded-full blur-xl" aria-hidden="true"></div>
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[rgb(130,88,18)]/10 rounded-full blur-xl" aria-hidden="true"></div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="/images/landingabt.png"
              alt="Intellectual Oasis Fellowship scholars collaborating in a virtual classroom"
              className="w-full h-[500px] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" aria-hidden="true"></div>
            
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex items-center gap-4 text-white">
                <div className="p-2 rounded-full bg-white/20 backdrop-blur-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">Virtual Collaboration</h3>
                  <p className="text-sm opacity-75">Research and learning across regions</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default About;